"use client";

import { useUser } from "@clerk/nextjs";
import { User, UserContext, UserError, UserGroup } from "./user-context";
import { useCallback, useEffect, useMemo, useState } from "react";
import { MobileTheme } from "@/app/types/types";
import {
  MOBILE_THEME_EXECUTIVE_DARK_GREEN,
  MOBILE_THEME_EXECUTIVE_GREEN,
} from "../mobile/styles/mobileTheme";
import { useQuery } from "@tanstack/react-query";
import {
  UserFetchError,
  getCartrackedUserByEmail,
} from "@/lib/functions/get-user-query";

export const UserContextWrapper = ({ children }) => {
  const { user: clerkUser, isSignedIn, isLoaded } = useUser();
  const [userGroups, setUserGroups] = useState<UserGroup[]>([]);

  const [isMobile, setIsMobile] = useState<boolean>(false);
  const [mobileTheme, setMobileTheme] = useState<MobileTheme>(
    MOBILE_THEME_EXECUTIVE_GREEN,
  );

  const email = clerkUser?.primaryEmailAddress?.emailAddress;

  const { data, isPending, isError, error } = useQuery({
    queryKey: ["user", email],
    queryFn: () => getCartrackedUserByEmail(email || ""),
    enabled: !!email,
    // A 404 means there is no row for this email and never will be until an
    // admin creates one, so hammering it five more times only delays the
    // message. Everything else (401 on a stale Clerk cookie, 5xx, network) gets
    // backed off rather than burned through in four seconds, which is not long
    // enough for Clerk to re-issue a session token after a wake from sleep.
    retry: (failureCount, err) =>
      !(err instanceof UserFetchError && err.status === 404) &&
      failureCount < 3,
    retryDelay: (failureCount) => Math.min(1000 * 2 ** failureCount, 15000),
    // Identity is session-scoped: refetching it every time the tab regains
    // focus buys nothing and gives a transient 401 a chance to take down a
    // working session.
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
  });

  // Derived, never mirrored into state: a `useEffect` copy would leave one
  // committed render where the query has resolved but `user` is still
  // undefined, which the auth shell reads as a failure.
  const user = useMemo(
    () => (data ? ({ ...data, id: data._id } as User) : undefined),
    [data],
  );

  // React Query marks a query `error` even when it still holds cached data, so
  // only escalate when there is genuinely nothing to show. Every branch that
  // leaves a signed-in visitor without a user must land on an error: falling
  // through to `null` would render the app shell with no identity behind it.
  const userError: UserError | null = user
    ? null
    : !isLoaded || !isSignedIn
      ? null
      : !email
        ? "no-email"
        : isError
          ? error instanceof UserFetchError && error.status === 404
            ? "not-found"
            : "fetch-failed"
          : isPending
            ? null
            : "fetch-failed";

  useEffect(() => {
    if (!userError) {
      return;
    }
    console.error(
      `Failed to resolve user: ${userError}`,
      error instanceof UserFetchError ? `status ${error.status}` : error,
    );
  }, [userError, error]);

  const getUserGroups = useCallback(
    async (dealershipId: string) => {
      if (!user) {
        return [];
      }

      const response = await fetch(
        `/v2/api/user/${user.id}/groups?dealershipId=${dealershipId}`,
      );
      if (!response.ok) {
        console.error("Failed to fetch user groups");
        return [];
      }

      const data: UserGroup[] = await response.json();

      return data;
    },
    [user],
  );

  // Mobile detection
  useEffect(() => {
    const handleDeviceDetection = () => {
      const userAgent = navigator.userAgent.toLowerCase();
      const isMobileDev = /iphone|ipod|android|blackberry|windows phone/g.test(
        userAgent,
      );
      const isTablet =
        /(ipad|tablet|playbook|silk)|(android(?!.*mobile))/g.test(userAgent);

      if (isMobileDev || isTablet) {
        setIsMobile(true);
      } else {
        setIsMobile(false);
      }
    };

    handleDeviceDetection();
    window.addEventListener("resize", handleDeviceDetection);

    return () => {
      window.removeEventListener("resize", handleDeviceDetection);
    };
  }, []);

  //dark mode detection
  useEffect(() => {
    const matchMedia = window.matchMedia("(prefers-color-scheme: dark)");

    const handleChange = (e) => {
      if (e.matches) {
        setMobileTheme(MOBILE_THEME_EXECUTIVE_DARK_GREEN);
      } else {
        setMobileTheme(MOBILE_THEME_EXECUTIVE_GREEN);
      }
    };
    handleChange(matchMedia);
    matchMedia.addEventListener("change", handleChange);

    return () => {
      matchMedia.removeEventListener("change", handleChange);
    };
  }, []);

  const getPrimaryUserGroup = useCallback(
    (dealershipId) => {
      if (!userGroups || userGroups.length < 1) {
        return;
      }

      return userGroups
        .filter((group) => group.dealershipId === dealershipId)
        .sort((a, b) => a.priority - b.priority)[0];
    },
    [userGroups],
  );

  const ctx: UserContext = {
    // A disabled query reports `isPending` too, so gate on there being an email
    // to look up: without one the query never runs and the skeleton would spin
    // forever. That case is surfaced as `no-email` instead.
    isLoading: !isLoaded || (!!isSignedIn && !!email && isPending),
    isAuthed: isSignedIn && user ? true : false,
    userError,
    user,
    userGroups,
    setUser: () => {},
    setUserGroups,
    getPrimaryUserGroup,
    hasAdminAccess: false,
    setHasAdminAccess: () => {},
    getAuthToken: () => "",
    getUserGroups,
    isMobile,
    setIsMobile,
    mobileTheme,
    setMobileTheme,
  };

  return <UserContext.Provider value={ctx}>{children}</UserContext.Provider>;
};
